'use client'

import { useState } from 'react'

interface BackInStockNotifyProps {
  productId: string
  variantId?: string
}

type State = 'idle' | 'loading' | 'success' | 'alreadySubscribed' | 'error'

export default function BackInStockNotify({ productId, variantId }: BackInStockNotifyProps) {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<State>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || state === 'loading') return

    setState('loading')
    setErrorMsg('')

    try {
      const res = await fetch('/api/back-in-stock/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), productId, variantId }),
      })

      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        setErrorMsg(data?.error || 'Something went wrong. Please try again.')
        setState('error')
        return
      }

      setState(data?.alreadySubscribed ? 'alreadySubscribed' : 'success')
    } catch {
      setErrorMsg('Network error. Please try again.')
      setState('error')
    }
  }

  if (state === 'success') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span>We&apos;ll email you when this is back in stock.</span>
      </div>
    )
  }

  if (state === 'alreadySubscribed') {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        You&apos;re already on the list for this item.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-2">
      <p className="text-sm font-medium text-ui-fg-base">Notify me when back in stock</p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          required
          placeholder="your@email.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            if (state === 'error') setState('idle')
          }}
          disabled={state === 'loading'}
          className="h-10 flex-1 rounded-lg border border-ui-border-base bg-ui-bg-field px-3 text-sm text-ui-fg-base placeholder:text-ui-fg-muted focus:border-ui-border-interactive focus:outline-none disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={state === 'loading' || !email.trim()}
          className="h-10 rounded-lg bg-ui-bg-base-pressed px-4 text-sm font-medium text-ui-fg-base transition-colors hover:bg-ui-bg-subtle-hover disabled:cursor-not-allowed disabled:opacity-50"
          style={{ minWidth: '80px' }}
        >
          {state === 'loading' ? (
            <span className="flex items-center justify-center gap-1">
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </span>
          ) : (
            'Notify me'
          )}
        </button>
      </form>
      {state === 'error' && (
        <p className="text-xs text-red-600">{errorMsg}</p>
      )}
    </div>
  )
}
